import { useMemo, useState } from "react";
import { Ban, CalendarArrowUp, CalendarCheck2, ChevronLeft, ChevronRight, Clock, Repeat, Sofa, Sun, X } from "lucide-react";
import { dueFromDateTime, parseDue, toISODate, type Due } from "@todoer/shared";
import { useUser } from "../lib/db.js";
import { addDaysISO, dayDate, friendlyDate } from "../lib/format.js";
import { Popover } from "./overlay.js";
import { cx, IconButton } from "./ui.js";

const pad = (n: number) => String(n).padStart(2, "0");

export function localTime(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Next occurrence of a weekday (0 = Sunday), never today. */
function nextWeekday(target: number, from = new Date()): string {
  const d = new Date(from);
  const diff = (target - d.getDay() + 7) % 7 || 7;
  d.setDate(d.getDate() + diff);
  return toISODate(d);
}

const fmtPick = (iso: string, withDate: boolean) =>
  dayDate(iso).toLocaleDateString([], withDate ? { weekday: "short", day: "numeric", month: "short" } : { weekday: "short" });

export function quickPicks(now = new Date()) {
  const today = toISODate(now);
  const weekend = now.getDay() === 6 || now.getDay() === 0 ? nextWeekday(6, new Date(now.getTime() + 86_400_000)) : nextWeekday(6, now);
  return [
    { key: "today", label: "Today", date: today, icon: CalendarCheck2, color: "var(--color-success)", hint: fmtPick(today, false) },
    { key: "tomorrow", label: "Tomorrow", date: addDaysISO(today, 1), icon: Sun, color: "var(--color-warning)", hint: fmtPick(addDaysISO(today, 1), false) },
    { key: "next-week", label: "Next week", date: nextWeekday(1, now), icon: CalendarArrowUp, color: "#8b5cf6", hint: fmtPick(nextWeekday(1, now), true) },
    { key: "weekend", label: "Weekend", date: weekend, icon: Sofa, color: "var(--color-info)", hint: fmtPick(weekend, true) },
  ];
}

/** Human summary of a due: "Tomorrow 15:00", "Every monday". */
export function describeDue(due: Due): string {
  if (due.isRecurring) return due.string.charAt(0).toUpperCase() + due.string.slice(1);
  return `${friendlyDate(due.date)}${due.datetime ? ` ${localTime(due.datetime)}` : ""}`;
}

/**
 * Date and time chooser used by quick add, the task panel, task rows and bulk edit.
 * Type naturally at the top ("fri 5pm", "every monday"), pick a quick option, a day
 * on the month grid, or set a time. `onPicked` fires when a choice is complete.
 */
export function DuePicker({ value, onChange, onPicked, dateOnly }: { value: Due | null; onChange: (due: Due | null) => void; onPicked?: () => void; dateOnly?: boolean }) {
  const user = useUser();
  const sow = user?.startOfWeek ?? 1;
  const today = toISODate(new Date());
  const [text, setText] = useState("");
  const [month, setMonth] = useState(() => { const d = value ? dayDate(value.date) : new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const time = value?.datetime ? localTime(value.datetime) : "";
  const parsed = useMemo(() => (text.trim() ? parseDue(text.trim()) : null), [text]);

  const pick = (due: Due | null) => { onChange(due); onPicked?.(); };
  const pickDate = (date: string) => pick(dueFromDateTime(date, dateOnly ? null : time || null));
  const applyText = () => { if (parsed) { setText(""); pick(dateOnly ? dueFromDateTime(parsed.due.date, null) : parsed.due); } };

  const cells = useMemo(() => {
    const first = new Date(month);
    const offset = (first.getDay() - sow + 7) % 7;
    const start = new Date(first);
    start.setDate(1 - offset);
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [month, sow]);
  const weekdays = cells.slice(0, 7).map((d) => d.toLocaleDateString([], { weekday: "narrow" }));
  const rows = cells.slice(35).every((d) => d.getMonth() !== month.getMonth()) ? cells.slice(0, 35) : cells;

  return (
    <div className="text-body">
      <div className="border-b border-border p-2">
        <input data-autofocus value={text} onChange={(e) => setText(e.target.value)} aria-label="Type a date"
          placeholder={dateOnly ? 'Type a date: "next friday", "1 dec"' : 'Type a date: "fri 5pm", "every monday"'}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); applyText(); } }}
          className="h-9 w-full rounded-control bg-surface-sunken px-3 text-body outline-none placeholder:text-text-faint focus:ring-2 focus:ring-brand/25 phone:h-11" />
        {text.trim() && (
          <button type="button" onClick={applyText} disabled={!parsed}
            className={cx("mt-1 flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-left text-meta", parsed ? "bg-brand-soft text-brand-text hover:brightness-95" : "text-text-faint")}>
            {parsed ? <>{parsed.due.isRecurring ? <Repeat size={14} aria-hidden /> : <CalendarCheck2 size={14} aria-hidden />}<span className="flex-1">{describeDue(parsed.due)}{parsed.due.isRecurring && ` · next ${friendlyDate(parsed.due.date)}`}</span><span className="opacity-70">Enter</span></>
              : "Not recognised yet. Try \"next tuesday 9am\"."}
          </button>
        )}
      </div>

      <ul className="py-1" aria-label="Quick dates">
        {quickPicks().map((q) => {
          const Icon = q.icon;
          const on = value?.date === q.date && !value.isRecurring;
          return (
            <li key={q.key}>
              <button type="button" onClick={() => pickDate(q.date)} aria-pressed={on}
                className={cx("mx-1 flex h-8 w-[calc(100%-8px)] items-center gap-2.5 rounded-control px-2 text-left hover:bg-hover phone:h-12", on && "bg-hover font-medium")}>
                <Icon size={16} style={{ color: q.color }} aria-hidden />
                <span className="flex-1">{q.label}</span>
                <span className="text-meta text-text-faint">{q.hint}</span>
              </button>
            </li>
          );
        })}
        <li>
          <button type="button" onClick={() => pick(null)} disabled={!value}
            className="mx-1 flex h-8 w-[calc(100%-8px)] items-center gap-2.5 rounded-control px-2 text-left hover:bg-hover disabled:opacity-40 phone:h-12">
            <Ban size={16} className="text-text-faint" aria-hidden /><span className="flex-1">No date</span>
          </button>
        </li>
      </ul>

      <div className="border-t border-border px-2 pb-2 pt-1.5">
        <div className="mb-1 flex items-center gap-1">
          <span className="flex-1 pl-1 text-meta font-semibold">{month.toLocaleDateString([], { month: "long", year: "numeric" })}</span>
          <IconButton size="sm" icon={ChevronLeft} label="Previous month" onClick={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))} />
          <IconButton size="sm" icon={ChevronRight} label="Next month" onClick={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))} />
        </div>
        <div className="grid grid-cols-7 text-center" role="grid" aria-label="Choose a day">
          {weekdays.map((w, i) => <div key={i} className="pb-1 text-caption font-medium text-text-faint" aria-hidden>{w}</div>)}
          {rows.map((d) => {
            const iso = toISODate(d);
            const inMonth = d.getMonth() === month.getMonth();
            const selected = value?.date === iso;
            const isToday = iso === today;
            return (
              <button key={iso} type="button" onClick={() => pickDate(iso)} aria-label={d.toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" })} aria-pressed={selected}
                className={cx("tabular mx-auto grid h-8 w-8 place-items-center rounded-full text-meta transition-colors duration-150 phone:h-11 phone:w-11 phone:text-body",
                  selected ? "bg-brand-strong font-semibold text-white" : isToday ? "font-semibold text-brand-text ring-1 ring-brand/50 ring-inset hover:bg-brand-soft" : "hover:bg-hover",
                  !inMonth && !selected && "text-text-faint", iso < today && !selected && !isToday && "opacity-60")}>
                {d.getDate()}
              </button>
            );
          })}
        </div>
      </div>

      {!dateOnly && <div className="flex items-center gap-2 border-t border-border px-3 py-2">
        <Clock size={16} className="text-text-faint" aria-hidden />
        <label htmlFor="due-time" className="flex-1 text-text-muted">Time</label>
        <input id="due-time" type="time" aria-label="Due time" value={time}
          onChange={(e) => onChange(value?.isRecurring && !e.target.value ? value : dueFromDateTime(value?.date ?? today, e.target.value || null))}
          className="tabular h-8 rounded-control border border-border bg-surface-raised px-2 text-body outline-none focus:border-brand phone:h-11" />
        {time && <IconButton size="sm" icon={X} label="Remove time" onClick={() => onChange(dueFromDateTime(value!.date, null))} />}
      </div>}
      {value?.isRecurring && !dateOnly && (
        <p className="flex items-center gap-2 border-t border-border px-3 py-2 text-meta text-success"><Repeat size={14} aria-hidden />Repeats {value.string}. Picking a day replaces the repeat.</p>
      )}
    </div>
  );
}

/** DuePicker in a popover (sheet on phone). Closes after a choice. */
export function DuePopover({ anchor, value, onChange, onClose }: { anchor: HTMLElement; value: Due | null; onChange: (due: Due | null) => void; onClose: () => void }) {
  return (
    <Popover anchor={anchor} onClose={onClose} title="Date" width={300}>
      <DuePicker value={value} onChange={onChange} onPicked={onClose} />
    </Popover>
  );
}
